import React, { useEffect, useState } from 'react';
type Props = {
  tone?: 'light' | 'dark';
};
export function Logo({
  tone = 'light'
}: Props) {
  const text = tone === 'light' ? 'text-cream' : 'text-ink';
  return <a href="/" className={`inline-flex items-center gap-2.5 transition-colors duration-300 ${text}`} aria-label="Kays&Kays home" data-id="element-304" data-mp="3qa35ujpykp5w">
      <LogoMark data-id="element-305" data-mp="3v8xq7q3kqls4" />
      <span className="whitespace-nowrap font-serif text-[21px] font-normal leading-none tracking-[-0.015em] opacity-90" data-id="element-306" data-mp="3v6bede9nhk43">
        Kays<span className="italic" data-id="element-307" data-mp="rsh2ecmspblk">&amp;</span>Kays
      </span>
    </a>;
}
const MARK_SRC = 'https://cdn.magicpatterns.com/uploads/9hLnuTMWz2SuF6t6GJnLfP/image.png';

// Glyph bounds inside the 1200×1200 source image
const SRC = {
  size: 1200,
  x: 176,
  y: 170,
  w: 848,
  h: 886
};

// Shared across every logo instance — the mask is built once per page load
let maskCache: string | null = null;
let maskPromise: Promise<string> | null = null;

/**
 * Cuts the glyph out of the uploaded image into a transparent mask: dark glyph pixels
 * become opaque, the coloured background becomes fully transparent.
 */
function buildMask(): Promise<string> {
  if (maskCache) return Promise.resolve(maskCache);
  if (maskPromise) return maskPromise;
  maskPromise = new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = SRC.w;
      c.height = SRC.h;
      const ctx = c.getContext('2d');
      if (!ctx) return reject(new Error('no canvas'));
      ctx.drawImage(img, SRC.x, SRC.y, SRC.w, SRC.h, 0, 0, SRC.w, SRC.h);
      const data = ctx.getImageData(0, 0, SRC.w, SRC.h);
      const px = data.data;
      for (let i = 0; i < px.length; i += 4) {
        const lum = (px[i] * 0.299 + px[i + 1] * 0.587 + px[i + 2] * 0.114) / 255;
        // Brightened + contrasted luminance, same curve the old filter used: bg → 1, glyph → 0
        const v = Math.min(1, Math.max(0, (lum * 3 - 0.5) * 2 + 0.5));
        px[i] = px[i + 1] = px[i + 2] = 0;
        px[i + 3] = Math.round((1 - v) * 255);
      }
      ctx.putImageData(data, 0, 0);
      maskCache = c.toDataURL('image/png');
      resolve(maskCache);
    };
    img.onerror = reject;
    img.src = MARK_SRC;
  });
  return maskPromise;
}

/** The mark, painted in the current text colour through a transparent mask — no background box. */
function LogoMark() {
  const [mask, setMask] = useState<string | null>(maskCache);
  useEffect(() => {
    if (mask) return;
    let alive = true;
    buildMask().then(m => alive && setMask(m)).catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [mask]);
  const scale = 22 / SRC.h; // render glyph at 22px tall
  const maskStyle = mask ? {
    WebkitMaskImage: `url(${mask})`,
    maskImage: `url(${mask})`,
    WebkitMaskSize: '100% 100%',
    maskSize: '100% 100%',
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat'
  } : {
    opacity: 0
  };
  return <span aria-hidden="true" className="block shrink-0 bg-current transition-opacity duration-300" style={{
    width: SRC.w * scale,
    height: SRC.h * scale,
    ...maskStyle
  }} data-id="element-308" data-mp="42o1pex11mfe" />;
}